import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../App';
import { BookOpen, Users, Plus, LogOut, FileText, ClipboardList } from 'lucide-react';

export default function ParentDashboard({ user, logout, setUser }) {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [childForm, setChildForm] = useState({
    name: '',
    birth_date: '',
    gender: '',
    grade_level: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [childrenRes, reportsRes, linkedRes] = await Promise.all([
        api.get('/children'),
        api.get('/reports'),
        api.get('/parents/my-students').catch(() => ({ data: { students: [] } }))
      ]);
      setChildren(childrenRes.data);
      setReports(reportsRes.data);
      setLinkedStudents(linkedRes.data?.students || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await api.post('/children', childForm);
      toast.success('Çocuk profili başarıyla eklendi');
      setDialogOpen(false);
      setChildForm({ name: '', birth_date: '', gender: '', grade_level: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to add child:', error);
      toast.error('Çocuk eklenirken hata oluştu');
    }
  };

  const startAssessment = async (childId, isLinkedStudent = false) => {
    try {
      // Check if student needs observer assessment
      let needsObserver = false;
      if (isLinkedStudent) {
        const info = await api.get(`/students/${childId}/assessment-info`);
        if (info.data.has_report) {
          toast.info('Bu ogrencinin raporu zaten mevcut');
          if (info.data.report_id) navigate(`/report/${info.data.report_id}`);
          return;
        }
        needsObserver = info.data.needs_observer;
      }
      
      const response = await api.post('/assessments', {
        assessment_type: 'parent_about_child',
        target_user_id: childId
      });
      
      if (needsObserver) {
        navigate(`/observer-assessment/${response.data.id}?target=${childId}`);
      } else {
        navigate(`/assessment/${response.data.id}`);
      }
    } catch (error) {
      console.error('Failed to start assessment:', error);
      toast.error('Envanter başlatılamadı');
    }
  };

  const startSelfAssessment = async () => {
    try {
      const response = await api.post('/assessments', {
        assessment_type: 'adult_self'
      });
      navigate(`/assessment/${response.data.id}`);
    } catch (error) {
      console.error('Failed to start self assessment:', error);
      toast.error('Envanter başlatılamadı');
    }
  };

  const handleStopImpersonation = async () => {
    try {
      const response = await api.post('/impersonate/stop');
      
      const { access_token, user: adminUser } = response.data;
      localStorage.setItem('access_token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(adminUser);
      
      toast.success('Süper admin hesabınıza döndünüz');
      navigate('/');
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      toast.error('Oturum sonlandırılamadı');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
              <p className="text-sm text-slate-600">Veli Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.impersonation?.active && (
              <Badge className="bg-orange-100 text-orange-700 animate-pulse">
                Taklit Modu: {user.impersonation.admin_name}
              </Badge>
            )}
            <span className="text-sm text-slate-600" data-testid="user-name">Merhaba, {user.name}</span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/coach')}
              className="bg-gradient-to-r from-blue-50 to-emerald-50 border-blue-200 hover:bg-blue-100"
            >
              💬 Koçumla Konuş
            </Button>
            {user.impersonation?.active ? (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleStopImpersonation}
                className="bg-orange-50 border-orange-200"
              >
                Taklidi Durdur
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8 animate-fade-in">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Veli Panelinize Hoş Geldiniz</h2>
          <p className="text-slate-600">Hem kendi hem de çocuklarınızın mizaç profillerini keşfedin</p>
        </div>

        {/* Self Assessment Section */}
        <Card className="shadow-lg border-none mb-8 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-purple-600" />
              Kendi Mizaç Envanterim
            </CardTitle>
            <CardDescription>Kendi mizaç profilinizi keşfedin</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm text-slate-600 mb-3">
                  Kendiniz için mizaç envanteri doldurarak kendi özelliklerinizi keşfedebilir, 
                  çocuğunuzla ilişkinizi daha iyi anlayabilirsiniz.
                </p>
                {reports.filter(r => r.user_id === user.id && r.target_user_id === user.id).length > 0 ? (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="border-purple-600 text-purple-700 hover:bg-purple-50"
                      onClick={() => navigate(`/report/${reports.filter(r => r.user_id === user.id && r.target_user_id === user.id)[0].id}`)}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Raporumu Görüntüle
                    </Button>
                    <Button
                      variant="outline"
                      onClick={startSelfAssessment}
                    >
                      <ClipboardList className="w-4 h-4 mr-2" />
                      Yeniden Doldur
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    onClick={startSelfAssessment}
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Kendi Envanterimi Doldur
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Children Section */}
        <Card className="shadow-lg border-none mb-8" data-testid="children-section">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Çocuklarım
                </CardTitle>
                <CardDescription>
                  {children.length > 0 
                    ? `${children.length} çocuk profili` 
                    : 'Çocuk profili ekleyerek başlayın'}
                </CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700" data-testid="add-child-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Çocuk Ekle
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Çocuk Profili Ekle</DialogTitle>
                    <DialogDescription>Çocuğunuzun bilgilerini girin</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddChild} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Ad Soyad</Label>
                      <Input
                        id="name"
                        value={childForm.name}
                        onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                        required
                        data-testid="child-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="birth_date">Doğum Tarihi</Label>
                      <Input
                        id="birth_date"
                        type="date"
                        value={childForm.birth_date}
                        onChange={(e) => setChildForm({ ...childForm, birth_date: e.target.value })}
                        required
                        data-testid="child-birthdate-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gender">Cinsiyet</Label>
                      <Select value={childForm.gender} onValueChange={(value) => setChildForm({ ...childForm, gender: value })} required>
                        <SelectTrigger data-testid="child-gender-select">
                          <SelectValue placeholder="Seçiniz" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kız">Kız</SelectItem>
                          <SelectItem value="Erkek">Erkek</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="grade_level">Sınıf Düzeyi</Label>
                      <Input
                        id="grade_level"
                        placeholder="Orn: 7. Sınıf"
                        value={childForm.grade_level}
                        onChange={(e) => setChildForm({ ...childForm, grade_level: e.target.value })}
                        required
                        data-testid="child-grade-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700" data-testid="submit-child-btn">
                      Kaydet
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : children.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {children.map((child) => {
                  const childReports = reports.filter(r => r.target_user_id === child.id);
                  const hasReport = childReports.length > 0;
                  
                  return (
                    <Card key={child.id} className="border shadow-sm" data-testid={`child-card-${child.id}`}>
                      <CardHeader>
                        <CardTitle className="text-lg">{child.name}</CardTitle>
                        <CardDescription>
                          {child.grade_level} • {child.gender}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => startAssessment(child.id)}
                          data-testid={`start-child-assessment-${child.id}`}
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Mizaç Formu Doldur
                        </Button>
                        {hasReport && (
                          <Button
                            variant="outline"
                            className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => navigate(`/report/${childReports[0].id}`)}
                            data-testid={`view-child-report-${child.id}`}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Raporu Görüntüle
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">Çocuk profili bulunmuyor</p>
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(true)}
                  data-testid="add-first-child-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  İlk Çocuk Profilini Ekle
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Linked Students from School */}
        {linkedStudents.length > 0 && (
          <Card className="shadow-lg border-none mb-8" data-testid="linked-students-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-orange-600" />
                Okul Ogrencilerim
              </CardTitle>
              <CardDescription>
                Okul tarafindan eslestirilen cocuklariniz
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {linkedStudents.map((student) => (
                  <Card key={student.id} className="border shadow-sm" data-testid={`linked-student-card-${student.id}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{student.name}</CardTitle>
                      <CardDescription>
                        {student.email}
                        {student.grade_level && ` - ${student.grade_level}. Sinif`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {student.is_young && !student.has_report && (
                        <Button
                          className="w-full bg-gradient-to-r from-amber-500 to-orange-500"
                          onClick={() => startAssessment(student.id, true)}
                          data-testid={`start-linked-assessment-${student.id}`}
                        >
                          <ClipboardList className="w-4 h-4 mr-2" />
                          Cocugum Icin Envanter Doldur
                        </Button>
                      )}
                      {student.has_report && student.report_id && (
                        <Button
                          variant="outline"
                          className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => navigate(`/report/${student.report_id}`)}
                          data-testid={`view-linked-report-${student.id}`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Raporu Goruntule
                        </Button>
                      )}
                      {student.is_young && (
                        <Badge className="bg-amber-100 text-amber-700">Gozlemci Formu Gerekli</Badge>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}